import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Upload, BarChart3, BookOpen, RotateCw } from "lucide-react";

const Index = () => (
  <div className="min-h-screen bg-gradient-to-b from-background to-secondary/30 dark:from-background dark:via-card dark:to-background">
    {/* Header */}
    <header className="border-b border-border/50 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">SC</span>
          </div>
          <span className="font-semibold text-lg">StudyCoach</span>
        </div>
        <Button variant="outline" className="gap-2">
          Sign In <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </header>

    {/* Hero Section */}
    <section className="max-w-6xl mx-auto px-4 py-20 md:py-32 text-center">
      <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
        Fix your grades <span className="text-primary">fast</span>
      </h1>
      <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
        Upload your test, get instant analysis of your mistakes, and receive a personalized study guide. StudyCoach helps college students master weak topics and improve their scores.
      </p>
      <Button size="lg" className="gap-2 h-12 px-8 text-base font-semibold">
        Start Analyzing <ArrowRight className="w-5 h-5" />
      </Button>
    </section>

    {/* Features Grid */}
    <section className="max-w-6xl mx-auto px-4 py-16 md:py-24">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">How It Works</h2>
      <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
        Four simple steps to smarter studying
      </p>

      <div className="grid md:grid-cols-4 gap-6">
        <FeatureCard
          icon={<Upload className="w-6 h-6" />}
          title="Upload Test"
          description="Photo, PDF, or paste text directly"
        />
        <FeatureCard
          icon={<BarChart3 className="w-6 h-6" />}
          title="AI Analysis"
          description="Identifies mistakes and weak topics"
        />
        <FeatureCard
          icon={<BookOpen className="w-6 h-6" />}
          title="Study Guide"
          description="Custom guide focused on your gaps"
        />
        <FeatureCard
          icon={<RotateCw className="w-6 h-6" />}
          title="Retest"
          description="Practice similar questions instantly"
        />
      </div>
    </section>

    {/* Benefits Section */}
    <section className="max-w-6xl mx-auto px-4 py-16 md:py-24 border-t border-border/50">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl font-bold mb-6">Study smarter, not harder</h2>
          <ul className="space-y-4">
            <li className="flex gap-3">
              <span className="text-accent font-bold">✓</span>
              <span>Understand exactly what you got wrong</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold">✓</span>
              <span>Focus study time on weak areas</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold">✓</span>
              <span>Practice with similar questions</span>
            </li>
            <li className="flex gap-3">
              <span className="text-accent font-bold">✓</span>
              <span>Track progress across tests</span>
            </li>
          </ul>
        </div>

        <Card className="p-8 bg-card border-border/50">
          <div className="space-y-4">
            <div className="h-2 bg-border rounded-full w-3/4"></div>
            <div className="h-2 bg-border rounded-full w-full"></div>
            <div className="h-2 bg-border rounded-full w-4/5"></div>
            <div className="pt-4 border-t border-border">
              <div className="h-3 bg-primary/10 rounded mt-4 w-1/3"></div>
            </div>
          </div>
        </Card>
      </div>
    </section>

    {/* CTA Section */}
    <section className="max-w-6xl mx-auto px-4 py-16 md:py-24">
      <div className="bg-primary text-primary-foreground rounded-lg p-12 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to improve your grades?</h2>
        <p className="text-lg opacity-90 mb-8">Join hundreds of college students already using StudyCoach</p>
        <Button size="lg" className="gap-2 bg-primary-foreground text-primary hover:bg-primary-foreground/90">
          Upload Your First Test <ArrowRight className="w-5 h-5" />
        </Button>
      </div>
    </section>

    {/* Footer */}
    <footer className="border-t border-border/50 py-8 text-center text-muted-foreground">
      <p>© 2026 StudyCoach. Fix your grades fast.</p>
    </footer>
  </div>
);

function FeatureCard({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <Card className="p-6 border-border/50 hover:border-border/80 transition-colors">
      <div className="text-primary mb-4">{icon}</div>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm">{description}</p>
    </Card>
  );
}

export default Index;